"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

/**
 * Near you (forecast areas spec, sections 5.14 and 10.2). After hydration it asks the
 * nearest-area API (approximate IP location, private, never cached) for each sport and shows a
 * small chip. Server HTML never varies by visitor. The chip never reorders rows: on the surf and
 * piste boards it links to the area page, on other boards it applies the area filter through
 * `onSelectArea`. Dismissible; the choice is remembered in local storage when available.
 */
export const NEAR_YOU_DISMISS_KEY = "pw-near-you-dismissed";

type NearArea = Readonly<{ slug: string; name: string; href: string; distanceKm: number }>;
type Found = Readonly<{ sport: string; label: string; area: NearArea }>;

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(NEAR_YOU_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.localStorage.setItem(NEAR_YOU_DISMISS_KEY, "1");
  } catch {
    // Private windows and blocked storage: the chip simply hides for this page view.
  }
}

async function fetchNearest(sport: string, signal: AbortSignal): Promise<NearArea | null> {
  try {
    const response = await fetch(`/api/conditions/nearest-area?sport=${encodeURIComponent(sport)}`, {
      cache: "no-store",
      signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { area?: NearArea | null };
    return body.area ?? null;
  } catch {
    return null;
  }
}

function distanceLabel(km: number): string {
  return km < 1 ? "under 1 km" : `${Math.round(km).toLocaleString("en-GB")} km`;
}

/**
 * Pure: the near-you results worth showing. On a filter board an area the board does not know
 * (no rows on it) would write `?area=` without filtering anything, so it is dropped.
 */
export function visibleNearYou<T extends Readonly<{ area: Readonly<{ slug: string }> }>>(
  found: readonly T[],
  knownArea: ((areaSlug: string) => boolean) | undefined,
): readonly T[] {
  return knownArea ? found.filter((entry) => knownArea(entry.area.slug)) : found;
}

export function BoardNearYouChip({
  sports,
  onSelectArea,
  activeArea = null,
  knownArea,
}: {
  /** Sport keys with a display label each, for example [{ sport: "surf", label: "Surf" }]. */
  sports: readonly Readonly<{ sport: string; label: string }>[];
  /** Filter boards: apply the area instead of following the link. */
  onSelectArea?: (areaSlug: string) => void;
  /** The board's current area filter, so the chip can show it is applied. */
  activeArea?: string | null;
  /** Filter boards: whether the board has rows in an area; other areas get no chip. */
  knownArea?: (areaSlug: string) => boolean;
}) {
  const [found, setFound] = useState<readonly Found[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const sportKey = sports.map((entry) => `${entry.sport}:${entry.label}`).join("|");

  useEffect(() => {
    if (readDismissed()) return;
    const controller = new AbortController();
    const wanted = sportKey.split("|").map((part) => {
      const [sport, label] = part.split(":");
      return { sport, label };
    });
    void Promise.all(wanted.map(async ({ sport, label }) => {
      const area = await fetchNearest(sport, controller.signal);
      return area ? { sport, label, area } : null;
    })).then((results) => {
      if (controller.signal.aborted) return;
      setFound(results
        .filter((result): result is Found => result !== null)
        .sort((a, b) => a.area.distanceKm - b.area.distanceKm));
    });
    return () => controller.abort();
  }, [sportKey]);

  const shown = visibleNearYou(found, knownArea);
  if (dismissed || shown.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" data-near-you>
      <span className="text-[12px] font-semibold text-text-muted">Near you:</span>
      {shown.map(({ sport, label, area }) => {
        const text = `${area.name}${shown.length > 1 ? ` ${label.toLowerCase()}` : ""}`;
        const title = `${area.name}, about ${distanceLabel(area.distanceKm)} away`;
        const className = "inline-flex min-h-11 items-center gap-1.5 rounded-full border border-teal/30 bg-teal/5 px-3.5 py-1.5 text-[12.5px] font-semibold text-teal-dark no-underline transition-colors hover:border-teal hover:bg-teal/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal";
        if (onSelectArea) {
          const active = activeArea === area.slug;
          return (
            <button
              key={sport}
              type="button"
              aria-pressed={active}
              title={title}
              onClick={() => onSelectArea(area.slug)}
              className={`${className} cursor-pointer ${active ? "border-teal bg-teal/10" : ""}`}
            >
              <PinIcon />
              {text}
            </button>
          );
        }
        return (
          <Link key={sport} href={area.href} title={title} className={className}>
            <PinIcon />
            {text}
          </Link>
        );
      })}
      <button
        type="button"
        onClick={() => {
          writeDismissed();
          setDismissed(true);
        }}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-text-muted transition-colors hover:text-text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
        aria-label="Hide the near you suggestion"
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function PinIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}
